import React, { useState } from 'react';
import { Routes, Route, useNavigate, useParams, Navigate, useLocation } from 'react-router-dom';
import './App.css';

// App component with routing configuration
import OrdersTray from './pages/OrdersTray';
import ConversationDetail from './pages/ConversationDetail';
import AllChats from './pages/AllChats';
import Clients from './pages/Clients';
import DailyReport from './pages/DailyReport';
import Settings from './pages/Settings';
import ExtractionAuditCheck from './pages/ExtractionAuditCheck';
import MenuOverlay from './components/MenuOverlay';
import ProtectedRoute from './components/ProtectedRoute';
import UpdateAvailableBanner from './components/UpdateAvailableBanner';
import { useAuth } from './context/AuthContext';

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleNavigateToHome = () => navigate('/');
  const handleNavigateToOrders = () => navigate('/');
  const handleNavigateToAllChats = () => navigate('/chats');
  const handleNavigateToClients = () => navigate('/clientes');
  const handleNavigateToDailyReport = () => navigate('/reporte-diario');
  const handleNavigateToSettings = () => navigate('/configuracion');

  return (
    <>
      <UpdateAvailableBanner />
      <Routes>
        {/* "/" redirects to "/orders" — but NOT during an Auth0 callback.
            A synchronous <Navigate> would strip ?code&state before the SDK
            consumes it and cause an infinite login loop. See
            feedback_auth_stable_routes memory. */}
        <Route path="/" element={<RootRedirect />} />

        <Route path="/orders" element={
          <ProtectedRoute>
            <OrdersTray
              onOrderClick={(orderId) => navigate(`/pedidos/${orderId}`)}
              onAllChatsClick={() => navigate('/chats')}
              onMenuClick={() => setIsMenuOpen(true)}
              onNavigateToDailyReport={handleNavigateToDailyReport}
            />
          </ProtectedRoute>
        } />

        <Route path="/orders/:orderId" element={
          <ProtectedRoute>
            <OrdersTray
              onOrderClick={(orderId) => navigate(`/pedidos/${orderId}`)}
              onAllChatsClick={() => navigate('/chats')}
              onMenuClick={() => setIsMenuOpen(true)}
              onNavigateToDailyReport={handleNavigateToDailyReport}
            />
          </ProtectedRoute>
        } />

        <Route path="/chats/:conversationId?" element={
          <ProtectedRoute>
            <AllChatsWrapper
              onBack={() => navigate('/')}
              onMenuClick={() => setIsMenuOpen(true)}
              onNavigateToOrder={(orderId) => navigate(`/pedidos/${orderId}`)}
            />
          </ProtectedRoute>
        } />
        
        <Route path="/pedidos/:orderId" element={<PedidosRedirect />} />
        
        <Route path="/clientes" element={
          <ProtectedRoute>
            <Clients 
              onBack={() => navigate('/')}
              onMenuClick={() => setIsMenuOpen(true)}
              onCustomerClick={(customer) => {
                if (customer.conversationIds && customer.conversationIds.length > 0) {
                  navigate(`/chats/${customer.conversationIds[0]}`);
                }
              }}
            />
          </ProtectedRoute>
        } />
        
        <Route path="/reporte-diario" element={
          <ProtectedRoute>
            <DailyReport
              onBack={() => navigate('/')}
              onMenuClick={() => setIsMenuOpen(true)}
            />
          </ProtectedRoute>
        } />

        <Route path="/configuracion" element={<Navigate to="/configuracion/listas-de-precios" replace />} />
        <Route path="/configuracion/listas-de-precios/:listId" element={
          <ProtectedRoute>
            <Settings onMenuClick={() => setIsMenuOpen(true)} />
          </ProtectedRoute>
        } />
        <Route path="/configuracion/:tab" element={
          <ProtectedRoute>
            <Settings onMenuClick={() => setIsMenuOpen(true)} />
          </ProtectedRoute>
        } />

        {/* Internal extraction-audit review page */}
        <Route path="/check" element={
          <ProtectedRoute>
            <ExtractionAuditCheck />
          </ProtectedRoute>
        } />

        {/* Auth0 invitation redirect — preserve query params for SDK */}
        <Route path="/login" element={<LoginRedirect />} />
      </Routes>
      <MenuOverlay
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        onNavigateToHome={handleNavigateToHome}
        onNavigateToOrders={handleNavigateToOrders}
        onNavigateToAllChats={handleNavigateToAllChats}
        onNavigateToClients={handleNavigateToClients}
        onNavigateToDailyReport={handleNavigateToDailyReport}
        onNavigateToSettings={handleNavigateToSettings}
        onLogout={logout}
      />
    </>
  );
}

// Wrapper for AllChats to handle both /chats and /chats/:conversationId
function AllChatsWrapper({ onBack, onMenuClick, onNavigateToOrder }: {
  onBack: () => void;
  onMenuClick: () => void;
  onNavigateToOrder: (orderId: string) => void;
}) {
  const { conversationId } = useParams<{ conversationId?: string }>();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (conversationId && isMobile) {
    return (
      <ConversationDetail
        conversationId={conversationId}
        onBack={() => navigate('/chats')}
        onNavigateToOrder={onNavigateToOrder}
        onMenuClick={onMenuClick}
      />
    );
  }

  return (
    <AllChats
      onBack={onBack}
      onMenuClick={onMenuClick}
      onNavigateToOrder={onNavigateToOrder}
    />
  );
}


function PedidosRedirect() {
  const { orderId } = useParams<{ orderId: string }>();
  return <Navigate to={`/orders/${orderId}`} replace />;
}

// "/" redirects to "/orders" — but during an Auth0 callback we render a
// spinner instead, so the <Navigate> doesn't strip ?code&state from the URL
// before Auth0Provider's init effect consumes it. Once the SDK runs
// onRedirectCallback it will navigate to appState.returnTo (which defaults
// to "/"), this component re-renders without ?code, and the Navigate fires.
function RootRedirect() {
  const { search } = useLocation();
  const hasAuthCallback = new URLSearchParams(search).has('code');
  if (hasAuthCallback) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900" />
      </div>
    );
  }
  return <Navigate to="/orders" replace />;
}

// Handles Auth0 invitation links that redirect to /login?invitation=...
function LoginRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/${search}`} replace />;
}

export default App;
